import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchCategory, fetchTerms, fetchProducts } from "../lib/api";
import { TermCard } from "../components/TermCard";
import { ProductCard } from "../components/ProductCard";
import { FloralOrnament } from "../components/FloralOrnament";
import { ChevronLeft } from "lucide-react";

const PARFUMS_SUB_FAMILIES = [
  { key: "all",              label: "Tous" },
  { key: "eau-de-parfum",    label: "Eaux de parfum" },
  { key: "parfum-solide",    label: "Parfums solides" },
  { key: "brume",            label: "Brumes" },
  { key: "bougie",           label: "Bougies" },
  { key: "parfum-ambiance",  label: "Parfums d'ambiance" },
  { key: "edition-limitee",  label: "Édition limitée" },
];
const PARFUMS_GENDERS = [
  { key: "all",     label: "Tous" },
  { key: "femmes",  label: "Femmes" },
  { key: "hommes",  label: "Hommes" },
  { key: "unisexe", label: "Unisexe" },
  { key: "enfants", label: "Enfants" },
];

export default function Category() {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [terms, setTerms] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subFamily, setSubFamily] = useState("all");
  const [gender, setGender] = useState("all");

  useEffect(() => {
    setLoading(true);
    setSubFamily("all");
    setGender("all");
    Promise.all([
      fetchCategory(slug),
      fetchTerms({ category: slug }),
      fetchProducts({ category: slug }),
    ])
      .then(([c, t, p]) => {
        setCategory(c);
        setTerms(t);
        setProducts(p);
        document.title = `${c.name} – Glossaire Beckyliss`;
      })
      .catch(() => setCategory(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const isParfums = slug === "parfums";

  // Available sub-families / genders + filtered product list
  const { availableSubFamilies, availableGenders, filteredProducts } = useMemo(() => {
    if (!isParfums) {
      return { availableSubFamilies: [], availableGenders: [],
               filteredProducts: products };
    }
    const subCounts = {};
    products.forEach(p => {
      const k = p.sub_family || "autre";
      subCounts[k] = (subCounts[k] || 0) + 1;
    });
    const subs = PARFUMS_SUB_FAMILIES.filter(
      s => s.key === "all" || subCounts[s.key] > 0
    ).map(s => ({ ...s, count: s.key === "all" ? products.length : (subCounts[s.key] || 0) }));

    let filtered = products;
    if (subFamily !== "all") filtered = filtered.filter(p => p.sub_family === subFamily);

    const showGender = subFamily === "eau-de-parfum" || subFamily === "all";
    let genders = [];
    if (showGender) {
      const gCounts = {};
      filtered.forEach(p => {
        if (!p.gender) return;
        gCounts[p.gender] = (gCounts[p.gender] || 0) + 1;
      });
      genders = PARFUMS_GENDERS.filter(
        g => g.key === "all" || gCounts[g.key] > 0
      ).map(g => ({ ...g, count: g.key === "all" ? filtered.length : (gCounts[g.key] || 0) }));
    }
    if (gender !== "all") filtered = filtered.filter(p => p.gender === gender);
    return { availableSubFamilies: subs, availableGenders: genders,
             filteredProducts: filtered };
  }, [products, subFamily, gender, isParfums]);

  if (loading) {
    return <div className="max-w-editorial mx-auto px-6 py-32 text-center text-bl-textSoft">Chargement…</div>;
  }
  if (!category) {
    return (
      <div className="max-w-editorial mx-auto px-6 py-32 text-center" data-testid="category-not-found">
        <p className="font-serif text-3xl">Catégorie introuvable</p>
        <Link to="/" className="overline mt-6 inline-block">Retour à l'accueil</Link>
      </div>
    );
  }

  return (
    <div className="fade-in-up" data-testid={`category-page-${category.slug}`}>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-bl-border hero-bg-gradient">
        <div className={`absolute inset-0 -z-0 ${(category.image_url || '').startsWith('/categories/') ? 'opacity-10' : 'opacity-25'}`}>
          <img
            src={category.image_url}
            alt=""
            className={`w-full h-full ${(category.image_url || '').startsWith('/categories/') ? 'object-contain' : 'object-cover'}`}
          />
        </div>
        {/* Filigree floral ornament — top-right watermark */}
        <FloralOrnament
          className="floral-ornament floral-ornament--tr"
          variant="1"
          testId="hero-floral-ornament-tr"
        />
        {/* Filigree floral ornament — mirrored bottom-left for balance */}
        <FloralOrnament
          className="floral-ornament floral-ornament--bl"
          variant="2"
          testId="hero-floral-ornament-bl"
        />
        <div className="relative max-w-editorial mx-auto px-6 md:px-10 lg:px-14 py-24 md:py-32">
          <Link to="/" className="inline-flex items-center gap-2 text-sm overline" data-testid="back-home-link">
            <ChevronLeft size={14} /> Glossaire
          </Link>
          <div className="mt-10 grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-7">
              <span className="overline">Famille {category.letter}</span>
              <h1 className="font-serif font-light text-5xl md:text-7xl text-bl-text mt-3 leading-[1.05]">
                {category.name}
              </h1>
              <p className="font-serif italic text-2xl text-bl-textSoft mt-4">« {category.tagline} »</p>
            </div>
            <div className="md:col-span-5">
              <p className="text-base text-bl-textSoft leading-relaxed max-w-md">
                {category.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TERMS */}
      <section className="max-w-editorial mx-auto px-6 md:px-10 lg:px-14 py-20 md:py-24" data-testid="category-terms">
        <div className="flex items-baseline justify-between mb-10">
          <h2 className="font-serif text-3xl md:text-4xl">Le glossaire</h2>
          <span className="overline">{terms.length} entrées</span>
        </div>
        <div>
          {terms.map((t) => (
            <TermCard key={t.slug} term={t} categoryName={category.name} />
          ))}
          {terms.length === 0 && (
            <p className="text-bl-textSoft text-sm py-12">Aucun terme pour le moment.</p>
          )}
        </div>
      </section>

      {/* PRODUCTS */}
      {products.length > 0 && (
        <section className="bg-bl-soft border-t border-bl-border py-20 md:py-24" data-testid="category-products">
          <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-14">
            <div className="flex items-baseline justify-between mb-8">
              <div>
                <span className="overline">La maison</span>
                <h2 className="font-serif text-3xl md:text-4xl mt-2">Produits {category.name.toLowerCase()}</h2>
              </div>
              <span className="overline">{filteredProducts.length} pièces</span>
            </div>

            {isParfums && (
              <Link
                to="/quiz-parfum"
                data-testid="parfums-quiz-cta"
                className="mb-8 flex items-center justify-between gap-4 border border-bl-mauveDeep bg-bl-rose/25 hover:bg-bl-rose/45 p-5 transition-colors group"
              >
                <div>
                  <span className="overline text-bl-mauveDeep">Mini-quiz · 3 questions</span>
                  <p className="font-serif text-xl mt-1 leading-tight">
                    Trouve ton parfum <em className="italic text-bl-dore">idéal</em> en 30 secondes.
                  </p>
                </div>
                <span className="text-sm text-bl-mauveDeep font-medium border-b border-bl-mauveDeep group-hover:border-transparent whitespace-nowrap">
                  Lancer le quiz →
                </span>
              </Link>
            )}

            {isParfums && availableSubFamilies.length > 2 && (
              <div className="mb-6" data-testid="parfum-subfamily-tabs">
                <div className="flex flex-wrap gap-2">
                  {availableSubFamilies.map(s => {
                    const active = s.key === subFamily;
                    return (
                      <button
                        key={s.key}
                        onClick={() => { setSubFamily(s.key); setGender("all"); }}
                        data-testid={`subfamily-tab-${s.key}`}
                        className={`px-4 py-2 text-xs uppercase tracking-[0.14em] rounded-full border transition-all ${
                          active
                            ? "bg-bl-mauveDeep text-white border-bl-mauveDeep"
                            : "bg-transparent text-bl-textSoft border-bl-border hover:border-bl-mauveDeep hover:text-bl-mauveDeep"
                        }`}
                      >
                        {s.label} <span className="opacity-70 ml-1">({s.count})</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {isParfums && availableGenders.length > 2 && (
              <div className="mb-10" data-testid="parfum-gender-tabs">
                <div className="flex flex-wrap gap-2 text-xs">
                  {availableGenders.map(g => {
                    const active = g.key === gender;
                    return (
                      <button
                        key={g.key}
                        onClick={() => setGender(g.key)}
                        data-testid={`gender-tab-${g.key}`}
                        className={`px-3 py-1.5 rounded-full border transition-all ${
                          active
                            ? "bg-bl-rose text-bl-mauveDeep border-bl-mauveDeep"
                            : "bg-transparent text-bl-textSoft border-transparent hover:text-bl-mauveDeep"
                        }`}
                      >
                        {g.label} <span className="opacity-60 ml-0.5">· {g.count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {filteredProducts.map((p) => (
                <ProductCard key={p.slug} product={p} />
              ))}
            </div>
            {filteredProducts.length === 0 && (
              <p className="text-bl-textSoft text-sm py-12 text-center">
                Aucun parfum ne correspond à ce filtre.
              </p>
            )}
          </div>
        </section>
      )}

      {products.length === 0 && (
        <section className="bg-bl-soft border-t border-bl-border py-20 text-center" data-testid="category-products-empty">
          <div className="max-w-xl mx-auto px-6">
            <span className="overline">Bientôt</span>
            <h2 className="font-serif text-3xl md:text-4xl mt-3 leading-tight">
              Cette famille s'enrichit prochainement.
            </h2>
            <p className="mt-6 text-sm text-bl-textSoft leading-relaxed">
              Les produits Beckyliss correspondants seront ajoutés très bientôt.
              En attendant, parcourez les définitions du glossaire.
            </p>
            <a
              href="https://www.beckylisscosmetics.com/category/all-products"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center gap-2 border border-bl-text px-6 py-3 text-sm tracking-wide hover:bg-bl-text hover:text-bl-bg transition-colors"
              data-testid="empty-shop-link"
            >
              Voir le catalogue Beckyliss
            </a>
          </div>
        </section>
      )}
    </div>
  );
}
